import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { m } from 'framer-motion';

// Components
import RDPHeader from '../Components/Header/RDPHeader';
import RDPFooter from '../Components/Footers/RDPFooter';
import { fadeIn } from '../Functions/GlobalAnimations';
import Buttons from '../Components/Button/Buttons';

const ServicesPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: 'line-icon-Factory',
      title: 'Fabrication sur mesure',
      description: 'Création de bouteilles personnalisées selon vos besoins spécifiques avec maquettes 3D et prototypes.',
      delay: 0.2
    },
    {
      icon: 'line-icon-Shopping-Bag',
      title: 'Catalogue standard',
      description: 'Large gamme de produits disponibles : vins, bières, spiritueux, soft drinks, bocaux et plus encore.',
      delay: 0.3
    },
    {
      icon: 'line-icon-Consulting',
      title: 'Conseil technique',
      description: 'Accompagnement personnalisé pour le choix de vos contenants adaptés à vos besoins et contraintes.',
      delay: 0.4
    },
    {
      icon: 'line-icon-Brush',
      title: 'Personnalisation',
      description: 'Sérigraphie, étiquetage, décoration et bouchons personnalisés pour valoriser votre marque.',
      delay: 0.5
    },
    {
      icon: 'line-icon-Truck',
      title: 'Livraison',
      description: 'Service de livraison fiable pour vos commandes sur la France et la Belgique.',
      delay: 0.6
    },
    {
      icon: 'line-icon-Dollar',
      title: 'Devis personnalisé',
      description: 'Établissement rapide de devis adaptés à votre projet et à votre volume de commande.',
      delay: 0.7
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Contact & Analyse',
      description: 'Vous nous contactez avec votre projet. Nous analysons vos besoins et contraintes techniques.',
      icon: 'line-icon-Phone-2'
    },
    {
      number: '02',
      title: 'Proposition & Devis',
      description: 'Nous vous proposons les solutions adaptées avec un devis détaillé et des échantillons si besoin.',
      icon: 'line-icon-File-ClipboardFileText'
    },
    {
      number: '03',
      title: 'Validation & Production',
      description: 'Après validation, nous lançons la fabrication ou préparons votre commande depuis notre stock.',
      icon: 'line-icon-Gear-2'
    },
    {
      number: '04',
      title: 'Livraison & Suivi',
      description: 'Livraison de votre commande avec suivi logistique et accompagnement après-vente.',
      icon: 'line-icon-Truck'
    }
  ];

  return (
    <div>
      {/* Header Start */}
      <RDPHeader />
      {/* Header End */}

      {/* Hero Section */}
      <section className="pt-[130px] pb-[130px] lg:pt-[120px] lg:pb-[90px] md:pt-[110px] md:pb-[75px] sm:pt-[100px] sm:pb-[50px] bg-white">
        <Container>
          <Row className="justify-center">
            <Col lg={10} className="text-center">
              <m.span
                className="text-glass-accent font-serif font-medium text-md uppercase block mb-[20px] tracking-[1px]"
                {...fadeIn}
              >
                Nos Services
              </m.span>
              <m.h1
                className="font-serif font-bold text-glass-darkest text-[3.5rem] lg:text-[2.8rem] md:text-[2.3rem] sm:text-[1.8rem] mb-[25px] leading-tight"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                Solutions complètes d'emballage en verre
              </m.h1>
              <m.p
                className="text-glass-medium text-xlg lg:text-lg md:text-md w-[85%] mx-auto leading-relaxed"
                {...{ ...fadeIn, transition: { delay: 0.4 } }}
              >
                De la conception à la livraison, nous vous accompagnons à chaque étape de votre projet d'emballage en verre.
              </m.p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Grid */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="gap-y-[30px]">
            {services.map((service, index) => (
              <Col lg={4} md={6} sm={12} key={index}>
                <m.div
                  className="bg-white rounded-[16px] p-[45px] text-center h-full group hover:shadow-xl transition-all duration-300 border border-glass-border"
                  {...{ ...fadeIn, transition: { delay: service.delay } }}
                >
                  <div className="relative inline-block mb-[25px]">
                    <div className="absolute inset-0 bg-glass-accent opacity-10 rounded-full scale-110 group-hover:scale-125 transition-transform duration-300"></div>
                    <i className={`${service.icon} text-glass-accent text-[55px] relative z-10`}></i>
                  </div>
                  <h4 className="font-serif font-bold text-glass-darkest mb-[18px] text-[1.4rem]">
                    {service.title}
                  </h4>
                  <p className="text-glass-medium text-[15px] leading-relaxed">
                    {service.description}
                  </p>
                </m.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Process Section */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="justify-center mb-[70px]">
            <Col lg={8} className="text-center">
              <m.h2
                className="font-serif font-bold text-glass-darkest text-[2.8rem] lg:text-[2.3rem] md:text-[1.9rem] mb-[20px]"
                {...fadeIn}
              >
                Notre processus de commande
              </m.h2>
              <m.p
                className="text-glass-medium text-lg"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                De votre premier contact à la livraison finale, un accompagnement sur mesure
              </m.p>
            </Col>
          </Row>

          <Row className="gap-y-[40px]">
            {processSteps.map((step, index) => (
              <Col lg={6} md={12} key={index}>
                <m.div
                  className="flex gap-[25px] items-start bg-white rounded-[16px] p-[40px] h-full border border-glass-border hover:shadow-lg transition-shadow"
                  {...{ ...fadeIn, transition: { delay: 0.3 + index * 0.1 } }}
                >
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-[70px] h-[70px] rounded-full bg-gradient-to-br from-glass-accent to-glass-accentHover flex items-center justify-center">
                        <span className="text-white font-bold text-[1.4rem] font-serif">{step.number}</span>
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-[35px] h-[35px] rounded-full bg-white shadow-md flex items-center justify-center">
                        <i className={`${step.icon} text-glass-accent text-[18px]`}></i>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-serif font-bold text-glass-darkest text-[1.3rem] mb-[12px]">
                      {step.title}
                    </h5>
                    <p className="text-glass-medium text-[15px] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </m.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white glass-separator-dots">
        <Container>
          <Row className="justify-center">
            <Col lg={10}>
              <m.div
                className="rounded-[24px] p-[60px] lg:p-[50px] md:p-[40px] text-center bg-white border border-glass-border"
                style={{
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)'
                }}
                {...fadeIn}
              >
                <div>
                  <h3 className="font-serif font-bold text-glass-darkest text-[2.3rem] lg:text-[2rem] md:text-[1.7rem] mb-[20px]">
                    Lancez votre projet avec RDP Glass
                  </h3>
                  <p className="text-glass-dark text-lg mb-[35px] max-w-[700px] mx-auto">
                    Que vous ayez besoin d'un catalogue standard ou d'une création sur mesure, notre équipe est prête à vous accompagner.
                  </p>
                  <div className="flex gap-[15px] justify-center sm:flex-col sm:items-center">
                    <Buttons
                      to="/produits"
                      className="bg-glass-accent text-white font-medium font-serif uppercase py-[14px] px-[40px] rounded-[8px] hover:bg-glass-accentHover transition-colors inline-block"
                      size="lg"
                      title="Voir le catalogue"
                    />
                    <Buttons
                      to="/contact"
                      className="bg-glass-darkest text-white font-medium font-serif uppercase py-[14px] px-[40px] rounded-[8px] hover:bg-gray-900 transition-colors inline-block"
                      size="lg"
                      title="Demander un devis"
                    />
                  </div>
                </div>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ / Additional Info */}
      <section className="pb-[130px] lg:pb-[90px] md:pb-[75px] sm:pb-[50px] bg-white">
        <Container>
          <Row className="justify-center mb-[60px]">
            <Col lg={8} className="text-center">
              <m.h2
                className="font-serif font-bold text-glass-darkest text-[2.5rem] lg:text-[2rem] md:text-[1.7rem] mb-[20px]"
                {...fadeIn}
              >
                Pourquoi choisir RDP Glass ?
              </m.h2>
            </Col>
          </Row>

          <Row className="gap-y-[30px]">
            <Col lg={6} md={12}>
              <m.div
                className="bg-white rounded-[16px] p-[40px] h-full border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.2 } }}
              >
                <div className="flex items-start gap-[20px] mb-[25px]">
                  <i className="line-icon-Handshake text-glass-accent text-[45px]"></i>
                  <div>
                    <h5 className="font-serif font-bold text-glass-darkest text-[1.2rem] mb-[10px]">
                      Partenaires européens exclusifs
                    </h5>
                    <p className="text-glass-medium text-[15px] leading-relaxed">
                      Accédez à 9 verriers européens de renom, absents du marché français, pour des produits uniques et de qualité.
                    </p>
                  </div>
                </div>
              </m.div>
            </Col>

            <Col lg={6} md={12}>
              <m.div
                className="bg-white rounded-[16px] p-[40px] h-full border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.3 } }}
              >
                <div className="flex items-start gap-[20px] mb-[25px]">
                  <i className="line-icon-MaleFemale text-glass-accent text-[45px]"></i>
                  <div>
                    <h5 className="font-serif font-bold text-glass-darkest text-[1.2rem] mb-[10px]">
                      Plus de 15 ans d'expérience
                    </h5>
                    <p className="text-glass-medium text-[15px] leading-relaxed">
                      Depuis 2008, nous accompagnons les professionnels de l'agroalimentaire avec expertise et fiabilité.
                    </p>
                  </div>
                </div>
              </m.div>
            </Col>

            <Col lg={6} md={12}>
              <m.div
                className="bg-white rounded-[16px] p-[40px] h-full border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.4 } }}
              >
                <div className="flex items-start gap-[20px] mb-[25px]">
                  <i className="line-icon-Cube-Molecule2 text-glass-accent text-[45px]"></i>
                  <div>
                    <h5 className="font-serif font-bold text-glass-darkest text-[1.2rem] mb-[10px]">
                      Solutions personnalisées
                    </h5>
                    <p className="text-glass-medium text-[15px] leading-relaxed">
                      Conception 3D, prototypes et maquettes pour créer le contenant parfait adapté à votre projet.
                    </p>
                  </div>
                </div>
              </m.div>
            </Col>

            <Col lg={6} md={12}>
              <m.div
                className="bg-white rounded-[16px] p-[40px] h-full border border-glass-border"
                {...{ ...fadeIn, transition: { delay: 0.5 } }}
              >
                <div className="flex items-start gap-[20px] mb-[25px]">
                  <i className="line-icon-Recycling-2 text-glass-accent text-[45px]"></i>
                  <div>
                    <h5 className="font-serif font-bold text-glass-darkest text-[1.2rem] mb-[10px]">
                      Engagement écologique
                    </h5>
                    <p className="text-glass-medium text-[15px] leading-relaxed">
                      Le verre est 100% recyclable infiniment. Nous promouvons l'économie circulaire et la durabilité.
                    </p>
                  </div>
                </div>
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <RDPFooter />
    </div>
  );
};

export default ServicesPage;
